import React from "react";
import { Link } from "react-router-dom";
import { Mail, Phone, MapPin, Clock } from "lucide-react";
import { useT } from "@/i18n";

const Footer = () => {
  const { lang, t } = useT();
  const base = `/${lang}`;

  return (
    <footer
      data-testid="site-footer"
      className="mt-24 bg-[#0B2545] text-slate-200"
    >
      <div className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-8">
        <div className="grid gap-12 lg:grid-cols-4">
          {/* Brand + about */}
          <div className="lg:col-span-1">
            <div className="flex items-center gap-2">
              <span
                aria-hidden="true"
                className="inline-flex h-9 w-9 items-center justify-center rounded-md bg-white font-serif text-lg font-bold text-[#0B2545]"
              >
                F
              </span>
              <span className="font-serif text-lg font-bold text-white">
                {t("brand.name")}
              </span>
            </div>
            <h3 className="mt-6 font-serif text-base font-semibold text-white">
              {t("footer.about_title")}
            </h3>
            <p className="mt-3 text-sm leading-relaxed text-slate-300">
              {t("footer.about_body")}
            </p>
          </div>

          {/* Legal info */}
          <div>
            <h3 className="font-serif text-base font-semibold text-white">
              {t("footer.legal_title")}
            </h3>
            <ul className="mt-4 space-y-2 text-sm text-slate-300">
              <li data-testid="footer-legal-company">{t("footer.legal_company")}</li>
              <li data-testid="footer-legal-rfc">{t("footer.legal_rfc")}</li>
              <li data-testid="footer-legal-registration">{t("footer.legal_registration")}</li>
              <li data-testid="footer-legal-license">{t("footer.legal_license")}</li>
              <li data-testid="footer-legal-jurisdiction">{t("footer.legal_jurisdiction")}</li>
              <li data-testid="footer-legal-incorporation">{t("footer.legal_incorporation")}</li>
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h3 className="font-serif text-base font-semibold text-white">
              {t("footer.contact_title")}
            </h3>
            <ul className="mt-4 space-y-3 text-sm text-slate-300">
              <li className="flex gap-3">
                <MapPin className="mt-0.5 h-4 w-4 shrink-0 text-[#16A34A]" />
                <span data-testid="footer-contact-address">
                  {t("footer.contact_address")}
                </span>
              </li>
              <li className="flex gap-3">
                <Phone className="mt-0.5 h-4 w-4 shrink-0 text-[#16A34A]" />
                <a
                  href={`tel:${t("footer.contact_phone").replace(/\s+/g, "")}`}
                  data-testid="footer-contact-phone"
                  className="hover:text-white"
                >
                  {t("footer.contact_phone")}
                </a>
              </li>
              <li className="flex gap-3">
                <Mail className="mt-0.5 h-4 w-4 shrink-0 text-[#16A34A]" />
                <a
                  href={`mailto:${t("footer.contact_email")}`}
                  data-testid="footer-contact-email"
                  className="hover:text-white"
                >
                  {t("footer.contact_email")}
                </a>
              </li>
              <li className="flex gap-3">
                <Clock className="mt-0.5 h-4 w-4 shrink-0 text-[#16A34A]" />
                <span data-testid="footer-contact-hours">
                  {t("footer.contact_hours")}
                </span>
              </li>
            </ul>
          </div>

          {/* Links */}
          <div>
            <h3 className="font-serif text-base font-semibold text-white">
              {t("footer.links_title")}
            </h3>
            <ul className="mt-4 space-y-2 text-sm">
              <li>
                <Link
                  to={`${base}/privacy`}
                  data-testid="footer-link-privacy"
                  className="text-slate-300 hover:text-white"
                >
                  {t("footer.link_privacy")}
                </Link>
              </li>
              <li>
                <Link
                  to={`${base}/cookies`}
                  data-testid="footer-link-cookies"
                  className="text-slate-300 hover:text-white"
                >
                  {t("footer.link_cookies")}
                </Link>
              </li>
              <li>
                <Link
                  to={`${base}/terms`}
                  data-testid="footer-link-terms"
                  className="text-slate-300 hover:text-white"
                >
                  {t("footer.link_terms")}
                </Link>
              </li>
            </ul>
          </div>
        </div>

        <div className="mt-12 border-t border-white/10 pt-8">
          <p
            data-testid="footer-disclaimer"
            className="text-xs leading-relaxed text-slate-400"
          >
            {t("footer.disclaimer")}
          </p>
          <p
            data-testid="footer-copyright"
            className="mt-4 text-xs text-slate-500"
          >
            {t("footer.copyright")}
          </p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
